import * as THREE from 'three';
export const DEFAULT_E57_IMAGE_CORRECTION = {
    enabled: true,
    azimuthDegrees: -90,
    flipVertical: true,
    flipHorizontal: false,
};
export function normalizeE57ImageCorrection(value) {
    return { ...DEFAULT_E57_IMAGE_CORRECTION, ...(value ?? {}) };
}
export function e57ImageCorrectionEquals(a, b) {
    return (a.enabled === b.enabled &&
        a.azimuthDegrees === b.azimuthDegrees &&
        a.flipVertical === b.flipVertical &&
        a.flipHorizontal === b.flipHorizontal);
}
function resolve(correction, representation) {
    if (!correction.enabled) {
        return { azimuth: 0, flipX: false, flipY: false };
    }
    // Azimuth is meaningless for a pinhole frame and mirroring a panorama would
    // break its wrap, so each representation only takes the fix that fits it.
    if (representation === 'pinhole') {
        return { azimuth: 0, flipX: correction.flipHorizontal, flipY: correction.flipVertical };
    }
    return {
        azimuth: THREE.MathUtils.degToRad(correction.azimuthDegrees),
        flipX: false,
        flipY: false,
    };
}
export function isCorrectionActiveFor(correction, representation) {
    const resolved = resolve(correction, representation);
    return resolved.azimuth !== 0 || resolved.flipX || resolved.flipY;
}
/**
 * Corrects a ray unprojected from a pixel, i.e. the display direction.
 * Mirrors are self-inverse; the rotation runs opposite to the projection side.
 */
export function correctUnprojectedRay(point, correction, representation) {
    const { azimuth, flipX, flipY } = resolve(correction, representation);
    if (flipX) {
        point.x = -point.x;
    }
    if (flipY) {
        point.y = -point.y;
    }
    if (azimuth !== 0) {
        point.applyAxisAngle(new THREE.Vector3(0, 0, 1), -azimuth);
    }
    return point;
}
/**
 * The same correction for the projection direction, as a matrix applied to a
 * point already in the image frame. Used when sampling colours for points, so
 * they land where the drawn plane says they should.
 */
export function correctionMatrixForProjection(correction, representation) {
    const { azimuth, flipX, flipY } = resolve(correction, representation);
    const matrix = new THREE.Matrix4();
    if (flipX || flipY) {
        matrix.multiply(new THREE.Matrix4().makeScale(flipX ? -1 : 1, flipY ? -1 : 1, 1));
    }
    if (azimuth !== 0) {
        matrix.premultiply(new THREE.Matrix4().makeRotationZ(azimuth));
    }
    return matrix;
}
export function describeE57ImageCorrection(correction, representation) {
    if (!correction.enabled) {
        return 'off — E57 pose and projection used exactly as stored';
    }
    const { azimuth, flipX, flipY } = resolve(correction, representation);
    const parts = [];
    if (azimuth !== 0) {
        const degrees = THREE.MathUtils.radToDeg(azimuth);
        parts.push(`${degrees > 0 ? '+' : ''}${degrees.toFixed(1)}° azimuth`);
    }
    if (flipY) {
        parts.push('flipped vertically');
    }
    if (flipX) {
        parts.push('flipped horizontally');
    }
    return parts.length ? parts.join(', ') : 'on, but nothing to change for this representation';
}
